import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Archive, Trash, ArrowLeft, Pencil, Check, X, Loader2, ImagePlus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { usePageCoverImage } from '@/hooks/content-generator/useWorkbookCoverImage';
import TabbedImageUploadModal from '@/components/ui/upload/TabbedImageUploadModal';

import WorkbookTagsDropdown from './WorkbookTagsDropdown';

interface WorkbookHeaderProps {
  pageId: string;
  title: string;
  isArchived: boolean;
  coverImage?: string;
  onToggleArchive: () => void;
  onDelete: () => void;
  onCoverImageChange?: (url: string | undefined) => void;
  onCreateTag?: () => void;
  onUpdateTitle?: (id: string, title: string) => void;
  // Loading states for button feedback
  isArchiveToggling?: boolean;
  isDeleting?: boolean;
  // Ownership props
  isOwner?: boolean;
  isCheckingOwnership?: boolean;
}

const WorkbookHeader: React.FC<WorkbookHeaderProps> = ({
  pageId,
  title,
  isArchived,
  coverImage,
  onToggleArchive,
  onDelete,
  onCoverImageChange,
  onCreateTag,
  onUpdateTitle,
  isArchiveToggling = false,
  isDeleting = false,
  isOwner = true, // Default to true for backward compatibility
  isCheckingOwnership = false,
}) => {
  const navigate = useNavigate();
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [tempTitle, setTempTitle] = useState(title);
  const inputRef = useRef<HTMLInputElement>(null);
  const [showImageModal, setShowImageModal] = useState(false);
  
  const { uploadCoverImage } = usePageCoverImage({
    pageId,
    initialCoverImageUrl: coverImage
  });

  useEffect(() => {
    setTempTitle(title);
  }, [title]);

  // Auto-resize input to fit content (same as PageHeader)
  useEffect(() => {
    if (inputRef.current && isEditingTitle) {
      // Create a temporary span to measure text width
      const span = document.createElement('span');
      span.style.visibility = 'hidden';
      span.style.position = 'absolute';
      span.style.whiteSpace = 'nowrap';
      span.style.fontSize = '1.25rem'; // text-xl
      span.style.fontWeight = 'bold';
      span.style.fontFamily = getComputedStyle(inputRef.current).fontFamily;
      span.textContent = tempTitle || 'Workbook title...'; // Use placeholder if empty

      document.body.appendChild(span);
      const textWidth = span.offsetWidth;
      document.body.removeChild(span);

      // Set input width with some padding
      const minWidth = 200; // Minimum width
      const maxWidth = 600; // Maximum width to prevent overflow
      const calculatedWidth = Math.min(Math.max(textWidth + 20, minWidth), maxWidth);

      inputRef.current.style.width = `${calculatedWidth}px`;
    }
  }, [tempTitle, isEditingTitle]);

  useEffect(() => {
    if (isEditingTitle && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditingTitle]);

  const handleSaveTitle = () => {
    const newTitle = tempTitle.trim() || 'Untitled Workbook';
    if (onUpdateTitle) {
      onUpdateTitle(pageId, newTitle);
    }
    setIsEditingTitle(false);
  };

  const handleCancelEdit = () => {
    setTempTitle(title);
    setIsEditingTitle(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveTitle();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };
  
  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Limit to 40 characters
    if (value.length <= 40) {
      setTempTitle(value);
    }
  };
  
  const handleGoBack = () => {
    navigate('/project-manager');
  };

  const handleImageUpload = async (file: File): Promise<string> => {
    try {
      const imageUrl = await uploadCoverImage(file);
      if (onCoverImageChange) {
        onCoverImageChange(imageUrl);
      }
      return imageUrl;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw new Error('Failed to upload image');
    }
  };

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          {isEditingTitle ? (
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={tempTitle}
                  onChange={handleTitleChange}
                  onKeyDown={handleKeyDown}
                  className="text-xl font-bold bg-transparent border-b-2 border-blue-500 outline-none text-transparent bg-clip-text bg-gradient-to-r from-purple-500 to-blue-400 dark:from-purple-400 dark:to-blue-300 caret-blue-500 dark:caret-blue-400"
                  placeholder="Workbook title..."
                  maxLength={40}
                />
                <motion.button
                  onClick={handleSaveTitle}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  className="h-8 w-8 flex items-center justify-center rounded-md text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-950/30 border border-transparent hover:border-green-200 dark:hover:border-green-800/50 transition-colors"
                  aria-label="Save title"
                  title="Save title"
                >
                  <Check size={16} />
                </motion.button>
                <motion.button
                  onClick={handleCancelEdit}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  className="h-8 w-8 flex items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 border border-transparent hover:border-red-200 dark:hover:border-red-800/50 transition-colors"
                  aria-label="Cancel editing"
                  title="Cancel editing"
                >
                  <X size={16} />
                </motion.button>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {tempTitle.length}/40 characters
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 group">
              <h1 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-500 to-blue-400 dark:from-purple-400 dark:to-blue-300">
                {title}
              </h1>
              <motion.button
                onClick={() => setIsEditingTitle(true)}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                className="h-8 w-8 flex items-center justify-center rounded-md text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-950/30 border border-transparent hover:border-gray-200 dark:hover:border-gray-800/50 transition-colors"
                aria-label="Edit workbook title"
                title="Edit workbook title"
                data-test-id="edit-title-button"
              >
                <Pencil size={14} />
              </motion.button>
            </div>
          )}
        </div>

        <div className="flex items-center gap-1 w-full sm:w-auto justify-start sm:justify-end">
          <WorkbookTagsDropdown 
            pageId={pageId}
            onCreateTag={onCreateTag}
          />
          
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            className="h-8 w-8 flex items-center justify-center rounded-md text-emerald-500 hover:bg-emerald-50 dark:hover:bg-emerald-950/30 border border-transparent hover:border-emerald-200 dark:hover:border-emerald-800/50 transition-colors"
            onClick={() => setShowImageModal(true)}
            data-test-id="cover-image-button"
            title={coverImage ? "Change cover image" : "Add cover image"}
          >
            <ImagePlus size={16} />
          </motion.button>
          
          {/* Only show archive button if user is owner */}
          {isOwner && (
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              className={cn(
                "h-8 w-8 flex items-center justify-center rounded-md border transition-colors",
                isArchived
                  ? "text-blue-500 bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800/50 hover:bg-blue-100 dark:hover:bg-blue-950/50"
                  : "text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-950/30 border-transparent hover:border-blue-200 dark:hover:border-blue-800/50",
                isArchiveToggling && "opacity-75"
              )}
              onClick={onToggleArchive}
              disabled={isArchiveToggling || isCheckingOwnership}
              data-test-id="archive-button"
              title={isArchived ? "Unarchive project" : "Archive project"}
            >
              {isArchiveToggling ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Archive size={16} />
              )}
            </motion.button>
          )}

          {/* Only show delete button if user is owner */}
          {isOwner && (
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              className={cn(
                "h-8 w-8 flex items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 border border-transparent hover:border-red-200 dark:hover:border-red-800/50 transition-colors",
                isDeleting && "opacity-75"
              )}
              onClick={onDelete}
              disabled={isDeleting || isCheckingOwnership}
              data-test-id="delete-button"
              title="Delete project permanently"
            >
              {isDeleting ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Trash size={16} />
              )}
            </motion.button>
          )}
        </div>
      </div>

      {/* Image Upload Modal */}
      <TabbedImageUploadModal
        isOpen={showImageModal}
        onClose={() => setShowImageModal(false)}
        onUpload={handleImageUpload}
        title={coverImage ? "Change Cover Image" : "Add Cover Image"}
        description="Upload or generate a cover image for your project"
        maxSizeMB={10}
        allowedTypes={['image/jpeg', 'image/png', 'image/webp', 'image/gif']}
        aspectRatio={16/9}
        accentColor="green"
        autoUpload={true}
        mode="cover"
      />
    </>
  );
};

export default WorkbookHeader;
